import React, { useState, useRef, useEffect, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import Select from "react-select";

const selectBoxStyle = {
  control: (base) => ({
    ...base,
    border: "1px solid #0C3B6C",
    boxShadow: "none",
    fontSize: "14px",
  }),
  singleValue: (base) => ({ ...base, fontSize: "14px" }),
  placeholder: (base) => ({ ...base, fontSize: "14px" }),
  input: (base) => ({ ...base, fontSize: "14px" }),
  multiValueLabel: (base) => ({ ...base, fontSize: "14px" }),
};

const TeacherEnrollment = () => {
  const initialFormData = {
    firstName: "",
    lastName: "",
    phoneNo: "",
    phoneNo2: "",
    email: "",
    gender: "",
    dateOfBirth: "",
    age: "",
    category: "",
    financial: "",
    status: "active",
    teacherType: "",
    state: "",
    city: "",
    address: "",
    classYouTeach: [],
    subjectYouTeach: [],
    qualification: "",
    profileImage: null,
    tenthDmc: null,
    plusTwoDmc: null,
    graduation: null,
    masters: null,
    diploma: null,
    experiencePeriod: "",
    organization: "",
    designation: "",
    session: "",
    personalAchievements: "",
    classIncharge: "",
    username: "",
    password: "",
    baseSalary: 0,
    allowances: 0,
    totalSalary: 0,
    joiningDate: "",
    sessionId: ""
  };

  const [formData, setFormData] = useState(initialFormData);
  const [loading, setLoading] = useState(false);
  const [editingTeacherId, setEditingTeacherId] = useState(null);

  const profileImage = useRef(null);
  const tenthDmcRef = useRef(null);
  const plusTwoDmcRef = useRef(null);
  const graduationRef = useRef(null);
  const mastersRef = useRef(null);
  const diplomaRef = useRef(null);

  const [teachers, setTeachers] = useState([]);
  const [filteredTeachers, setFilteredTeachers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);

  const [selectedClassIncharge, setSelectedClassIncharge] = useState("");
  const [sections, setSections] = useState([]);
  const [selectedSection, setSelectedSection] = useState("");
  const [designations, setDesignations] = useState([]);
  const [selectedClasses, setSelectedClasses] = useState([]);
  const [selectedSubjects, setSelectedSubjects] = useState([]);
  
const navigate = useNavigate();

const handleViewClick = (id) => {
  navigate(`/teachers/${id}`);
};



  // SESSION ID FROM LOCAL STORAGE 
  const sessionId = localStorage.getItem("currentSessionId");

  // FIXED STYLING
  const labelStyle = "block font-semibold text-black text-[18px] mb-2";
  const inputStyle =
    "mt-1 p-2 block w-full border border-gray-300 rounded-md text-[14px] focus:ring-indigo-500 focus:border-indigo-500";

  // Fetch classes for the current session
  useEffect(() => {
    if (!sessionId) return;

    axios
      .get(` ${process.env.REACT_APP_API_BASE_URL}/api/classes/getClasses?sessionId=${sessionId}`)
      .then((res) => setClasses(res.data))
      .catch((err) => console.error("Error fetching classes:", err));
  }, [sessionId]);

  // Auto fetch subjects when classes change
  useEffect(() => {
    const fetchSubjectsForTeaching = async () => {
      if (!sessionId) return;

      try {
        const allSubjects = [];

        for (const className of formData.classYouTeach) {
          const enc = encodeURIComponent(className);

          const res = await axios.get(
            ` ${process.env.REACT_APP_API_BASE_URL}/api/subjects/getSubjectsByClassName/${enc}`,
            { params: { sessionId } }
          );

          allSubjects.push(...res.data);
        }

        // Remove duplicate subjects
        const unique = Array.from(
          new Map(allSubjects.map((s) => [s.subjectName, s])).values()
        );

        setSubjects(unique);
      } catch (err) {
        console.error("Error fetching subjects:", err);
      }
    };

    if (formData.classYouTeach.length > 0 && sessionId) {
      fetchSubjectsForTeaching();
    } else {
      setSubjects([]);
      setFormData((prev) => ({ ...prev, subjectYouTeach: [] }));
    }
  }, [formData.classYouTeach, sessionId]);

  useEffect(() => {
    if (!sessionId) return;

    axios
      .get(` ${process.env.REACT_APP_API_BASE_URL}/api/teachers/getTeachers`,{params: { sessionId : localStorage.getItem("currentSessionId") }})
      .then((res) => {
        setTeachers(res.data);
        setFilteredTeachers(res.data);
      })
      .catch((err) => console.error("Error fetching teachers:", err));
  }, [sessionId]);


  const handleSalaryChange = useCallback((e, key) => {
    const value = parseFloat(e.target.value) || 0;
    setFormData((prev) => {
      const newData = {
        ...prev,
        [key]: value,
      };
      newData.totalSalary = (newData.baseSalary || 0) + (newData.allowances || 0);
      return newData;
    });
  }, []);

  useEffect(() => {
    const filtered = teachers.filter(
      (t) =>
        (t.firstName || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
        (t.lastName || "").toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredTeachers(filtered);
  }, [searchTerm, teachers]);

 
  const handleSectionChange = useCallback((opt) => {
    setSelectedSection(opt.value);

    setFormData((prev) => ({
      ...prev,
      classIncharge: `${selectedClassIncharge} - ${opt.value}`,
    }));
  }, [selectedClassIncharge]);

  const handleClassInchargeChange = useCallback(async (opt) => {
    const className = opt.value;
    setSelectedClassIncharge(className);
    setSelectedSection(""); 

    if (!className || !sessionId) return;

    const encoded = encodeURIComponent(className);

    try {
      const sectionRes = await axios.get(
        ` ${process.env.REACT_APP_API_BASE_URL}/api/sections/${encoded}/getSectionsByClassName`,
        { params: { sessionId } }
      );

      setSections(sectionRes.data || []);

      console.log("Sections Loaded:", sectionRes.data);

    } catch (err) {
      console.error("Error fetching sections:", err);
    }
  }, [sessionId]);

  const calculateAge = (dob) => {
    if (!dob) return "";
    const birth = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    if (
      today.getMonth() < birth.getMonth() ||
      (today.getMonth() === birth.getMonth() && today.getDate() < birth.getDate())
    ) {
      age--;
    }
    return age;
  };

  useEffect(() => {
    if (formData.firstName && formData.classIncharge && formData.dateOfBirth) {
      const year = new Date(formData.dateOfBirth).getFullYear();
      const random = Math.floor(1000 + Math.random() * 9000);

      const userName = `${formData.firstName}${year}${formData.classIncharge}`;
      const password = `${formData.firstName}${random}`;

      setFormData((prev) => ({
        ...prev,
        username: userName,
        password,
      }));
    }
  }, [formData.firstName, formData.classIncharge, formData.dateOfBirth]);

  const handleChange = useCallback((e) => {
    const { name, value, files } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: files ? files[0] : value,
      age: name === "dateOfBirth" ? calculateAge(value) : prev.age,
    }));
  }, []);

  const handleMultiSelectChange = (selected, key) => {
    setFormData({
      ...formData,
      [key]: selected ? selected.map((opt) => opt.value) : [],
    });
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete?")) return;
    try {
      await axios.delete(` ${process.env.REACT_APP_API_BASE_URL}/api/teachers/deleteTeacher/${id}`);
      const updated = teachers.filter((t) => t._id !== id);
      setTeachers(updated);
      setFilteredTeachers(updated);
    } catch (err) {
      console.error("Delete error:", err);
      alert("Error deleting teacher");
    }
  };

  const handleEdit = (teacher) => {
    if (!teacher) return;
    setEditingTeacherId(teacher._id || null);

    setFormData({
      firstName: teacher.firstName || "",
      lastName: teacher.lastName || "",
      phoneNo: teacher.phoneNo || "",
      phoneNo2: teacher.phoneNo2 || "",
      email: teacher.email || "",
      gender: teacher.gender || "",
      dateOfBirth: teacher.dateOfBirth ? new Date(teacher.dateOfBirth).toISOString().slice(0, 10) : "",
      age: teacher.dateOfBirth ? calculateAge(teacher.dateOfBirth) : "",
      category: teacher.category || "",
      financial: teacher.financial || "",
      status: teacher.status || "active",
      teacherType: teacher.teacherType || "",
      state: teacher.state || "",
      city: teacher.city || "",
      address: teacher.address || "",
      classYouTeach: teacher.classYouTeach || [],
      subjectYouTeach: teacher.subjectYouTeach || [],
      qualification: teacher.qualification || "",
      profileImage: null,
      tenthDmc: null,
      plusTwoDmc: null,
      graduation: null,
      masters: null,
      diploma: null,
      experiencePeriod: teacher.experiencePeriod || "",
      organization: teacher.organization || "",
      designation: teacher.designation ? teacher.designation.toString() : "",
      personalAchievements: teacher.personalAchievements || "",
      classIncharge: teacher.classIncharge || "",
      username: teacher.username || "",
      password: "",
      baseSalary: teacher.baseSalary || 0,
      allowances: teacher.allowances || 0,
      totalSalary: (teacher.baseSalary || 0) + (teacher.allowances || 0),
      joiningDate: teacher.joiningDate ? new Date(teacher.joiningDate).toISOString().slice(0, 10) : "",
    });

    setSelectedClasses(teacher.classYouTeach ? teacher.classYouTeach.map(cls => ({ value: cls, label: cls })) : []);
    setSelectedSubjects(teacher.subjectYouTeach ? teacher.subjectYouTeach.map(sub => ({ value: sub, label: sub })) : []);

    if (teacher.classIncharge) {
      const parts = String(teacher.classIncharge).split(" - ");
      if (parts.length === 2) {
        setSelectedClassIncharge(parts[0]);
        setSelectedSection(parts[1]);
      } else {
        setSelectedClassIncharge(teacher.classIncharge);
      }
    }

    if (teacher.classIncharge) {
      const classNameToFetch = teacher.classIncharge.split(" - ")[0] || teacher.classIncharge;
      axios
        .get(` ${process.env.REACT_APP_API_BASE_URL}/api/sections/${encodeURIComponent(classNameToFetch)}/getSectionsByClassName`, {
          params: { sessionId }
        })
        .then((res) => setSections(res.data || []))
        .catch((err) => console.error("Error loading sections for edit:", err));
    }
  };
   useEffect(() => {
    if (!sessionId) return;

    axios
      .get(`${process.env.REACT_APP_API_BASE_URL}/api/allDesignations/getDesignations`, {
        params: {
          sessionId,
          type: "Teacher", 
        },
        
      })
      .then((res) => {
        setDesignations(res.data);
      })
      .catch((err) => {
        console.error("Designation fetch error:", err);
      });
  }, [sessionId]);
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true) ;

    const form = new FormData();
    for (const key in formData) {
      if (key === "sessionId") {
        // send a single ObjectId string
        if (sessionId) form.append("sessionId", sessionId);
      } else if (Array.isArray(formData[key])) {
        form.append(key, JSON.stringify(formData[key]));
      } else {
        form.append(key, formData[key] ?? "");
      }
    }


    try {
      let resultTeacher;
      if (editingTeacherId) {
        const res = await axios.put(
          ` ${process.env.REACT_APP_API_BASE_URL}/api/teachers/updateTeacher/${editingTeacherId}`,
          form,
          { params: {sessionId },
            headers: { "Content-Type": "multipart/form-data" } }
        );
        resultTeacher = res.data;
        setTeachers((prev) => prev.map((t) => (t._id === editingTeacherId ? resultTeacher : t)));
        setFilteredTeachers((prev) => prev.map((t) => (t._id === editingTeacherId ? resultTeacher : t)));
        alert("Teacher updated");
      } else {
        const res = await axios.post(
          `${process.env.REACT_APP_API_BASE_URL}/api/teachers/createTeacher`,
          form,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
        resultTeacher = res.data;
        setTeachers((prev) => [...prev, resultTeacher]);
        setFilteredTeachers((prev) => [...prev, resultTeacher]);
        alert("Teacher added");
      }

      setFormData(initialFormData);
      setEditingTeacherId(null);
      setSelectedClasses([]);
      setSelectedSubjects([]);
      if (profileImage.current) profileImage.current.value = null;
      if (tenthDmcRef.current) tenthDmcRef.current.value = null;
      if (plusTwoDmcRef.current) plusTwoDmcRef.current.value = null;
      if (graduationRef.current) graduationRef.current.value = null;
      if (mastersRef.current) mastersRef.current.value = null;
      if (diplomaRef.current) diplomaRef.current.value = null;
    } catch (err) {
      console.error("Submit error:", err);
      alert("Error saving teacher. Check console.");
    } finally {
      setLoading(false);
    }
  };



  return (
    <div className="max-w-full mx-auto p-8 bg-white shadow-md rounded-lg">

      {/* MAIN TITLE  */}
      <h2 style={{ fontSize: "18px", fontWeight: 700, marginBottom: "24px" }}>Teacher Registration Form</h2>

      {/* FORM */}
      <form onSubmit={handleSubmit} encType="multipart/form-data">

        {/* GRID FORM */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

          {/* FIRST NAME */}
          <div>
            <label className={labelStyle}>First Name *</label>
            <input
              className={inputStyle}
              type="text"
              name="firstName"
              placeholder="Enter First Name"
              value={formData.firstName}
              onChange={handleChange}
              required
            />
          </div>

          {/* LAST NAME */}
          <div>
            <label className={labelStyle}>Last Name *</label>
            <input
              className={inputStyle}
              type="text"
              name="lastName"
              placeholder="Enter Last Name"
              value={formData.lastName}
              onChange={handleChange}
            />
          </div>

          {/* PHONE */}
          <div>
            <label className={labelStyle}>Phone Number *</label>
            <input
              className={inputStyle}
              type="number"
              name="phoneNo"
              placeholder="Enter Phone Number"
              value={formData.phoneNo}
              onChange={handleChange}
              required
            />
          </div>

          {/* ALT PHONE */}
          <div>
            <label className={labelStyle}>Alternate Phone Number</label>
            <input
              className={inputStyle}
              type="number"
              name="phoneNo2"
              placeholder="Enter Alternate Phone Number"
              value={formData.phoneNo2}
              onChange={handleChange}
            />
          </div>

          {/* EMAIL */}
          <div>
            <label className={labelStyle}>Email</label>
            <input
              className={inputStyle}
              type="email"
              name="email"
              placeholder="Enter Email"
              value={formData.email}
              onChange={handleChange}
              
            />
          </div>

          {/* GENDER */}
          <div>
            <label className={labelStyle}>Gender *</label>
            <select
              className={inputStyle}
              name="gender"
              value={formData.gender}
              onChange={handleChange}
              required
            >
              <option value="">Select Gender</option>
              <option value="Male">Male</option>
              <option value="Female">Female</option>
              <option value="Other">Other</option>
            </select>
          </div>

          {/* DOB */}
          <div>
            <label className={labelStyle}>Date of Birth</label>
            <input
              className={inputStyle}
              type="date"
              name="dateOfBirth"
              value={formData.dateOfBirth}
              onChange={handleChange}
            />
          </div>

          {/* AGE */}
          <div>
            <label className={labelStyle}>Age</label>
            <input
              className={inputStyle}
              type="number"
              name="age"
              value={formData.age}
              readOnly
            />
          </div>

          {/* CATEGORY */}
          <div>
            <label className={labelStyle}>Category</label>
            <select
              className={inputStyle}
              name="category"
              value={formData.category}
              onChange={handleChange}
              
            >
              <option value="">Select Category</option>
              <option value="General">General</option>
              <option value="SC">SC</option>
              <option value="ST">ST</option>
              <option value="OBC">OBC</option>
              <option value="Minority">Minority</option>
            </select>
          </div>

          {/* FINANCIAL CATEGORY */}
          <div>
            <label className={labelStyle}>Financial Category</label>
            <select
              className={inputStyle}
              name="financial"
              value={formData.financial}
              onChange={handleChange}
              
            >
              <option value="">Select</option>
              <option value="apl">APL</option>
              <option value="bpl">BPL</option>
              <option value="ews">EWS</option>
            </select>
          </div>

          {/* TEACHER STATUS */}
          <div>
            <label className={labelStyle}>Teacher Status *</label>
            <select
              className={inputStyle}
              name="status"
              value={formData.status}
              onChange={handleChange}
              required
            >
              <option value="active">Active</option>
              <option value="deactive">Deactive</option>
            </select>
          </div>

          {/* TEACHER TYPE */}
          <div>
            <label className={labelStyle}>Teacher Type</label>
            <select
              className={inputStyle}
              name="teacherType"
              value={formData.teacherType}
              onChange={handleChange}
              
            >
              <option value="">Select</option>
              <option value="PGT">PGT</option>
              <option value="TGT">TGT</option>
            </select>
          </div>

          {/* STATE */}
          <div>
            <label className={labelStyle}>State</label>
            <input
              className={inputStyle}
              type="text"
              name="state"
              placeholder="Enter State"
              value={formData.state}
              onChange={handleChange}
              
            />
          </div>

          {/* CITY */}
          <div>
            <label className={labelStyle}>City</label>
            <input
              className={inputStyle}
              type="text"
              name="city"
              placeholder="Enter City"
              value={formData.city}
              onChange={handleChange}
              
            />
          </div>

          {/* ADDRESS */}
          <div className="md:col-span-2">
            <label className={labelStyle}>Address *</label>
            <textarea
              className={inputStyle}
              name="address"
              placeholder="Enter Address"
              value={formData.address}
              onChange={handleChange}
              rows={3}
              required
            />
          </div>

          {/* QUALIFICATION */}
          <div>
            <label className={labelStyle}>Qualification</label>
            <input
              className={inputStyle}
              type="text"
              name="qualification"
              placeholder="Enter Qualification"
              value={formData.qualification}
              onChange={handleChange}
              
            />
          </div>
          {/* CLASS YOU TEACH */}
          <div className="md:col-span-2">
            <label className={labelStyle}>Class You Teach *</label>

            <Select
              isMulti
              options={classes.map((c) => ({
                value: c.className,
                label: c.className
              }))}
              value={selectedClasses}
              onChange={(selected) => {
                setSelectedClasses(selected || []);
                setFormData((prev) => ({
                  ...prev,
                  classYouTeach: selected ? selected.map((s) => s.value) : []
                }));
              }}
              styles={selectBoxStyle}
              placeholder="Select Classes"
            />
          </div>
          {subjects.length > 0 && (
            <div>
              <label className={labelStyle}>Subjects You Teach *</label>

              <Select
                isMulti
                options={subjects.map((s) => ({
                  value: s.subjectName,
                  label: s.subjectName
                }))}
                value={selectedSubjects}
                onChange={(selected) => {
                  setSelectedSubjects(selected || []);
                  setFormData((prev) => ({
                    ...prev,
                    subjectYouTeach: selected ? selected.map((s) => s.value) : []
                  }));
                }}
                styles={selectBoxStyle}
                placeholder="Select Subjects"
              />
            </div>
          )}
          {/* PROFILE IMAGE */}
          <div>
            <label className={labelStyle}>Profile Image{editingTeacherId ? " (Optional)" : ""}</label>
            <input className={inputStyle} type="file" name="profileImage" ref={profileImage} onChange={handleChange}  />
          </div>

          {/* DMC FILES */}
          <div>
            <label className={labelStyle}>10th DMC{editingTeacherId ? " (Optional)" : ""}</label>
            <input className={inputStyle} type="file" name="tenthDmc" ref={tenthDmcRef} onChange={handleChange}  />
          </div>
          <div>
            <label className={labelStyle}>12th DMC{editingTeacherId ? " (Optional)" : ""}</label>
            <input className={inputStyle} type="file" name="plusTwoDmc" ref={plusTwoDmcRef} onChange={handleChange}  />
          </div>
          <div>
            <label className={labelStyle}>Graduation{editingTeacherId ? " (Optional)" : ""}</label>
            <input className={inputStyle} type="file" name="graduation" ref={graduationRef} onChange={handleChange}  />
          </div>
          <div>
            <label className={labelStyle}>Masters (Optional)</label>
            <input className={inputStyle} type="file" name="masters" ref={mastersRef} onChange={handleChange} />
          </div>
          <div>
            <label className={labelStyle}>Diploma (Optional)</label>
            <input className={inputStyle} type="file" name="diploma" ref={diplomaRef} onChange={handleChange} />
          </div>

          {/* EXPERIENCE */}
          <div>
            <label className={labelStyle}>Experience Period</label>
            <input
              className={inputStyle}
              type="text"
              name="experiencePeriod"
              placeholder="Enter Experience"
              value={formData.experiencePeriod}
              onChange={handleChange}
            />
          </div>

          {/* ORGANIZATION */}
          <div>
            <label className={labelStyle}>Organization</label>
            <input
              className={inputStyle}
              type="text"
              name="organization"
              placeholder="Enter Organization"
              value={formData.organization}
              onChange={handleChange}
            />
          </div>

          {/* DESIGNATION */}
          {/* DESIGNATION */}
<div>
  <label className={labelStyle}>Designation *</label>
<select
  className={inputStyle}
  name="designation"
  value={formData.designation}
  onChange={handleChange}
  required
>
  <option value="">Select Designation</option>

  {designations.map((d) => (
    <option key={d._id} value={d._id}>
      {d.designationName}
    </option>
  ))}
</select>
</div>
{/* SALARY */}
          <div>
            <label className={labelStyle}>Base Salary *</label>
            <input
              className={inputStyle}
              type="number"
              name="baseSalary"
              value={formData.baseSalary}
              onChange={(e) => handleSalaryChange(e, "baseSalary")}
            />
          </div>
          <div>
            <label className={labelStyle}>Allowances *</label>
            <input
              className={inputStyle}
              type="number"
              name="allowances"
              value={formData.allowances}
              onChange={(e) => handleSalaryChange(e, "allowances")}
            />
          </div>
          <div>
            <label className={labelStyle}>Total Salary</label>
            <input
              className={inputStyle}
              type="number"
              name="totalSalary"
              value={formData.totalSalary}
              readOnly
            />
          </div>

          {/* JOINING DATE */}
          <div>
            <label className={labelStyle}>Joining Date *</label>
            <input
              className={inputStyle}
              type="date"
              name="joiningDate"
              value={formData.joiningDate}
              onChange={handleChange}
            />
          </div>

          {/* ACHIEVEMENTS */}
          <div className="md:col-span-2">
            <label className={labelStyle}>Personal Achievements (Optional)</label>
            <textarea
              className={inputStyle}
              name="personalAchievements"
              placeholder="Enter Achievements"
              value={formData.personalAchievements}
              onChange={handleChange}
              rows={3}
            />
          </div>

          {/* CLASS INCHARGE SECTION */}
          <div className="md:col-span-2">
            <label className={labelStyle}>Class Incharge (Assign Section) *</label>

            {/* CLASS DROPDOWN */}
            <Select
              className="mt-3"
              options={classes.map((c) => ({
                value: c.className,
                label: c.className
              }))}
              value={
                selectedClassIncharge
                  ? { value: selectedClassIncharge, label: selectedClassIncharge }
                  : null
              }
              onChange={handleClassInchargeChange}
              styles={selectBoxStyle}
              placeholder="Select Class"
            />

            {/* SECTION DROPDOWN */}
            {sections.length > 0 && (
              <Select
                className="mt-3"
                options={sections.map((s) => ({
                  value: s.sectionName,
                  label: s.sectionName
                }))}
                value={
                  selectedSection
                    ? { value: selectedSection, label: selectedSection }
                    : null
                }
                onChange={(opt) => {
                  setSelectedSection(opt.value);
                  setFormData((prev) => ({
                    ...prev,
                    classIncharge: `${selectedClassIncharge} - ${opt.value}`
                  }));
                }}
                styles={selectBoxStyle}
                placeholder="Select Section"
              />
            )}
          </div>

          {/*  */}
          <div>
            <label className={labelStyle}>UserName</label>
            <input className={inputStyle} type="text" name="username" value={formData.username} onChange={handleChange} readOnly />
          </div>

          {/* PASSWORD */}
          <div>
            <label className={labelStyle}>Password</label>
            <input className={inputStyle} type="text" name="password" value={formData.password} onChange={handleChange} readOnly />
          </div>
        </div>

        {/* BUTTONS */}
        <div className="mt-8 flex gap-6">
          <button
            type="submit"
            disabled={loading}
            style={{
              background: "#0C3B6C",
              color: "white",
              padding: "12px 30px",
              borderRadius: "8px",
              fontSize: "18px",
              fontWeight: "600",
            }}
          >
            {loading ? "Submitting..." : editingTeacherId ? "Update" : "Register"}
          </button>

          <button
            type="button"
            onClick={() => {
              setFormData(initialFormData);
              setEditingTeacherId(null);
              setSelectedClasses([]);
              setSelectedSubjects([]);
            }}
            style={{
              background: "#d9534f",
              color: "white",
              padding: "12px 30px",
              borderRadius: "8px",
              fontSize: "18px",
              fontWeight: "600",
            }}
          >
            Reset
          </button>
        </div>
      </form>

      {/* TABLE LIST */}
      <div className="mt-10">
        <h1 style={{ fontSize: "18px", fontWeight: 700, marginBottom: "12px" }}>Teacher Management</h1>

        {/* SEARCH */}
        <div className="flex items-center justify-between mb-4">
          <input
            type="text"
            className={inputStyle}
            placeholder="Search Teacher by Name"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />

          <p style={{ fontSize: "18px", fontWeight: 600 }}>
            Total Teachers: {filteredTeachers.length}
          </p>
        </div>

        {/* TABLE */}
        <div className="overflow-x-auto p-4">
          <table className="min-w-full border border-gray-300">
            <thead className="bg-gray-200">
              <tr>
                <th className="p-2 border text-lg font-semibold">Sr No.</th>
                <th className="p-2 border text-lg font-semibold">Name</th>
                <th className="p-2 border text-lg font-semibold">Subject</th>
                <th className="p-2 border text-lg font-semibold">Class Incharge</th>
                <th className="p-2 border text-lg font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredTeachers.length > 0 ? (
                filteredTeachers.map((teacher, index) => (
                  <tr key={teacher._id} className="text-center bg-gray-50 hover:bg-gray-100">
                    <td className="p-2 border text-lg">{index + 1}</td>
                    <td className="p-2 border text-lg">{teacher.firstName} {teacher.lastName}</td>
                   <td className="p-2 border text-lg">
  {(() => {
    let subjects = [];

    if (Array.isArray(teacher.subjectYouTeach)) {
      subjects = teacher.subjectYouTeach;
    } else if (typeof teacher.subjectYouTeach === "string") {
      try {
        subjects = JSON.parse(teacher.subjectYouTeach);
      } catch {
        subjects = [];
      }
    }

    return subjects.length > 0 ? subjects.join(", ") : "--";
  })()}
</td>

                    <td className="p-2 border text-lg">{teacher.classIncharge || "--"}</td>
                    <td className="p-2 border flex justify-center gap-2">
                     <button
  onClick={() => handleViewClick(teacher._id)}
  className="px-3 py-1 bg-green-600 text-white rounded text-sm md:text-base">
  View
</button>

                      <button
                        onClick={() => handleEdit(teacher)}
                        className="px-3 py-1 bg-yellow-500 text-white rounded text-sm md:text-base">
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(teacher._id)}
                        className="px-3 py-1 bg-red-600 text-white rounded text-sm md:text-base">
                        Delete
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={5} className="p-4 text-center text-gray-500">
                    No teachers found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

      </div>

    </div>
  );
};

export default TeacherEnrollment;